import { action, computed, makeObservable, observable } from 'mobx';

import { chatRowLayoutStore, computeDock } from './chat-row-layout.store';

const WIDTH_STORAGE_KEY = 'file-preview-panel-width';
const DEFAULT_WIDTH = 600;
export const MIN_PREVIEW_WIDTH = 360;
export const MAX_PREVIEW_WIDTH = 1100;

function loadWidth(): number {
  if (typeof window === 'undefined') return DEFAULT_WIDTH;
  const raw = Number(localStorage.getItem(WIDTH_STORAGE_KEY));
  if (
    Number.isFinite(raw) &&
    raw >= MIN_PREVIEW_WIDTH &&
    raw <= MAX_PREVIEW_WIDTH
  ) {
    return raw;
  }
  return DEFAULT_WIDTH;
}

class FilePreviewPanelStore {
  isOpen = false;
  threadId: string | null = null;
  fileId: string | null = null;
  width = loadWidth();
  isFullscreen = false;

  constructor() {
    makeObservable(this, {
      isOpen: observable,
      threadId: observable,
      fileId: observable,
      width: observable,
      isFullscreen: observable,
      availableWidth: computed,
      dock: computed,
      canDock: computed,
      isFloating: computed,
      dockedWidth: computed,
      open: action,
      close: action,
      setWidth: action,
      toggleFullscreen: action,
    });
  }

  get availableWidth(): number {
    return chatRowLayoutStore.previewAvailableWidth;
  }

  get dock() {
    return computeDock(this.availableWidth, this.width, MIN_PREVIEW_WIDTH);
  }

  get canDock(): boolean {
    return this.dock.canDock;
  }

  get isFloating(): boolean {
    return this.isFullscreen || !this.canDock;
  }

  get dockedWidth(): number {
    return this.dock.dockedWidth;
  }

  open(threadId: string, fileId: string): void {
    this.isOpen = true;
    this.threadId = threadId;
    this.fileId = fileId;
    this.isFullscreen = false;
  }

  close(): void {
    this.isOpen = false;
    this.threadId = null;
    this.fileId = null;
    this.isFullscreen = false;
  }

  setWidth(width: number): void {
    const clamped = Math.min(
      MAX_PREVIEW_WIDTH,
      Math.max(MIN_PREVIEW_WIDTH, Math.round(width))
    );
    this.width = clamped;
    if (typeof window !== 'undefined') {
      localStorage.setItem(WIDTH_STORAGE_KEY, String(clamped));
    }
  }

  toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
  }
}

export const filePreviewPanelStore = new FilePreviewPanelStore();
